import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { UploadCertificateResponse } from '../../models/certificate.model';

/**
 * Service HTTP pour la gestion des certificats de scolarité
 */
@Injectable({
  providedIn: 'root'
})
export class CertificateService {
  private readonly apiUrl = `${environment.apiUrl}/labase/certificate-verification`;

  constructor(private http: HttpClient) {}

  /**
   * Vérifie uniquement un certificat sans l'uploader (pour l'inscription)
   * @param file - Fichier du certificat (image ou PDF)
   * @returns Résultat de la vérification OCR
   */
  async verifyCertificateOnly(file: File): Promise<{ message: string; verification: any }> {
    const formData = new FormData();
    formData.append('file', file);

    return firstValueFrom(
      this.http.post<{ message: string; verification: any }>(`${this.apiUrl}/verify-only`, formData)
    );
  }

  /**
   * Upload et vérifie un certificat de scolarité
   * @param userId - ID de l'utilisateur LaBase (0 pour l'inscription)
   * @param file - Fichier du certificat (image ou PDF)
   * @param email - Email de l'utilisateur (requis si userId = 0 pour récupérer l'UUID)
   * @returns Résultat de la vérification avec l'URL publique
   */
  async uploadCertificate(userId: string, file: File, email?: string, prenom?: string, nom?: string): Promise<UploadCertificateResponse> {
    const formData = new FormData();
    formData.append('userId', userId);
    formData.append('file', file);

    if (email) formData.append('email', email);
    if (prenom) formData.append('prenom', prenom);
    if (nom) formData.append('nom', nom);

    return firstValueFrom(
      this.http.post<UploadCertificateResponse>(`${this.apiUrl}/upload`, formData)
    );
  }

  /**
   * Valide les formats de fichier acceptés
   * @param file - Fichier à valider
   * @returns true si le format est accepté
   */
  validateFileFormat(file: File): boolean {
    const acceptedTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/webp',
      'application/pdf'
    ];
    return acceptedTypes.includes(file.type);
  }

  /**
   * Valide la taille du fichier (max 10MB)
   * @param file - Fichier à valider
   * @returns true si la taille est acceptable
   */
  validateFileSize(file: File): boolean {
    const maxSize = 10 * 1024 * 1024; // 10MB
    return file.size <= maxSize;
  }

  /**
   * Obtient un message d'erreur pour un fichier invalide
   * @param file - Fichier à valider
   * @returns Message d'erreur ou null si valide
   */
  getFileValidationError(file: File): string | null {
    if (!this.validateFileFormat(file)) {
      return 'Format de fichier non accepté. Veuillez uploader une image (JPEG, PNG, WEBP) ou un PDF.';
    }
    if (!this.validateFileSize(file)) {
      return 'Le fichier est trop volumineux. Taille maximale : 10 MB.';
    }
    return null;
  }
}
